/**
 * 이 **소셜 글이 광고로 돌고 있나** — 색인이 준 값만으로 가른다(새 조회 없음).
 *
 * ```
 *   adMeta.adBoost: true        「광고집행」   ← 가장 단단한 신호(플랫폼이 직접 말한 것)
 *   adMeta.adType: commission   「틱톡샵」
 *   adMeta.adType: paid         「협찬」
 * ```
 *
 * 🚨 **이름은 `PostKindChips` 가 정본**이다 — 글 카드·표·상세가 이미 쓰는 말이다.
 *   ⛔ 「커미션」·「유료 광고」로 적지 마라(색인 값 이름을 그대로 노출한 것이다). 한 화면에서 같은
 *   것을 두 이름으로 부르면 다른 것으로 읽힌다.
 *
 * 🔑 **돈이 들어간 글인지**를 가리는 자리다 — 훑을 때 오가닉 반응과 광고 반응이 섞이면
 *   「이 브랜드는 그냥 잘 되네」를 잘못 읽는다.
 * ⚠️ **없는 것이 「아니다」가 아니다** — 값이 붙은 글이 채널마다 다르다(틱톡 `adBoost` 83% ·
 *   인스타 `adType` 49%). 값이 없으면 **아무 표도 붙이지 않는다**(「오가닉」이라 적으면 거짓이다).
 * ⚠️ 인스타의 `paid` 는 **0.4%**다 — 적게 잡히는 것이지 인스타에 광고가 적은 것이 아니다.
 *   같은 소재가 Meta 광고로 도는지는 **상세의 「비슷한 소재」**가 이미지로 대조한다.
 */
export function readAdRun(post: Record<string, unknown> | undefined): AdRun | null {
  const meta = post?.adMeta;
  if (!meta || typeof meta !== 'object') return null;
  const box = meta as { adBoost?: unknown; adType?: unknown };

  // 플랫폼이 직접 「광고로 돌고 있다」고 말한 것이 가장 세다
  if (box.adBoost === true) return { kind: 'boost', label: '광고집행', hint: '플랫폼이 광고로 표시한 글입니다' };

  const type = String(box.adType ?? '').trim().toLowerCase();
  if (type === 'paid') return { kind: 'paid', label: '협찬', hint: '협찬으로 표기된 글입니다' };
  if (type === 'commission') return { kind: 'commission', label: '틱톡샵', hint: '틱톡샵 링크가 붙은 글입니다' };
  return null;
}

/** 광고로 돌고 있다는 표 — `kind` 로 색을 가른다 */
export type AdRun = {
  kind: 'boost' | 'paid' | 'commission';
  label: string;
  hint: string;
};

/**
 * **같은 소재가 Meta 광고로도 도는가** — 상세가 이미 받아 둔 「비슷한 소재」로 가린다(새 조회 없음).
 *
 * 🔑 `tier: 'near'` 는 **pHash 복제**다 — 「닮았다」가 아니라 «같은 그림»이라는 뜻이라, 이것만 센다.
 * ⛔ `clip`(의미 유사)·`template`(색·레이아웃)로 판정하지 마라 — 닮은 남의 광고를 「이 글이 광고로
 *   돈다」로 말해 버린다. 그 둘은 「비슷한 소재」 줄에 그대로 두면 된다.
 * ⚠️ 집행 중인 것을 먼저 본다 — 끝난 광고는 「돌고 있다」가 아니다.
 */
export function findAdTwin(
  ads: { tier?: string; isActive?: boolean; adArchiveId: string; pageName?: string; url?: string }[],
): { adArchiveId: string; pageName: string; url: string; active: boolean } | null {
  const twins = ads.filter((ad) => ad.tier === 'near' && ad.adArchiveId);
  if (twins.length === 0) return null;
  const pick = twins.find((ad) => ad.isActive === true) ?? twins[0];
  return {
    adArchiveId: pick.adArchiveId,
    pageName: String(pick.pageName ?? '').trim(),
    url: String(pick.url ?? '').trim(),
    active: pick.isActive === true,
  };
}
